import React, { useMemo } from 'react';
import { AssetExpense } from '../AssetDetailScreen';
import { AddExpenseForm } from './AddExpenseForm';
import { ExpenseList } from './ExpenseList';
import { Plus, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

interface AssetExpensesTabProps {
  expenses: AssetExpense[];
  newExpense: {
    item: string;
    category: 'Initial Cost' | 'Maintenance';
    localCost: string;
    foreignCost: string;
    date: string;
    note: string;
    personInCharge: string;
  };
  setNewExpense: React.Dispatch<
    React.SetStateAction<{
      item: string;
      category: 'Initial Cost' | 'Maintenance';
      localCost: string;
      foreignCost: string;
      date: string;
      note: string;
      personInCharge: string;
    }>
  >;
  isAddingExpense: boolean;
  handleAddExpense: (e: React.FormEvent) => Promise<void>;
  handleEditExpense: (expense: AssetExpense) => void;
  editingExpenseId: string | null;
  handleDeleteExpense: (expenseId: string) => Promise<void>;
  expenseReceiptRef: React.RefObject<HTMLInputElement>;
  itemInputRef: React.RefObject<HTMLInputElement>;
  currency: string;
  overseasCurrency: string;
  isExpenseModalOpen: boolean;
  setIsExpenseModalOpen: (open: boolean) => void;
}

export function AssetExpensesTab({
  expenses,
  newExpense,
  setNewExpense,
  isAddingExpense,
  handleAddExpense,
  handleEditExpense,
  editingExpenseId,
  handleDeleteExpense,
  expenseReceiptRef,
  itemInputRef,
  currency,
  overseasCurrency,
  isExpenseModalOpen,
  setIsExpenseModalOpen,
}: AssetExpensesTabProps) {
  const formatMoney = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      amount
    );

  const totals = useMemo(() => {
    return expenses.reduce(
      (acc, curr) => {
        const cost = curr.localCost || 0;
        acc.total += cost;
        if (curr.category === 'Initial Cost') acc.initial += cost;
        else acc.maintenance += cost;
        return acc;
      },
      { total: 0, initial: 0, maintenance: 0 }
    );
  }, [expenses]);

  return (
    <div className="space-y-8">
      {/* Totals Summary */}
      <div className="grid grid-cols-3 items-center justify-between gap-2">
        <div className="rounded-2xl bg-zinc-900 p-3 text-center dark:bg-zinc-100">
          <p className="text-[8px] font-bold tracking-tighter text-zinc-400 uppercase dark:text-zinc-500">
            Total
          </p>
          <p className="text-xs font-black text-white dark:text-zinc-900">
            {formatMoney(totals.total)}
          </p>
        </div>
        <div className="rounded-2xl border border-zinc-100 bg-zinc-50 p-3 text-center dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-[8px] font-bold tracking-tighter text-zinc-400 uppercase">
            Initial
          </p>
          <p className="text-xs font-bold text-zinc-900 dark:text-zinc-100">
            {formatMoney(totals.initial)}
          </p>
        </div>
        <div className="rounded-2xl border border-zinc-100 bg-zinc-50 p-3 text-center dark:border-zinc-800 dark:bg-zinc-900">
          <p className="text-[8px] font-bold tracking-tighter text-zinc-400 uppercase">
            Maintenance
          </p>
          <p className="text-xs font-bold text-zinc-900 dark:text-zinc-100">
            {formatMoney(totals.maintenance)}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between border-b border-zinc-100 pb-4 dark:border-zinc-800">
        <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
          Records
        </h3>
        <button
          onClick={() => {
            setNewExpense({
              item: '',
              category: 'Maintenance',
              localCost: '',
              foreignCost: '',
              date: new Date().toISOString().split('T')[0],
              note: '',
              personInCharge: '',
            });
            setIsExpenseModalOpen(true);
          }}
          className="flex items-center gap-2 rounded-xl bg-zinc-900 px-4 py-2 text-xs font-bold tracking-wider text-white uppercase shadow-sm transition-opacity hover:opacity-90 dark:bg-zinc-100 dark:text-zinc-900"
        >
          <Plus className="h-4 w-4" />
          Add Record
        </button>
      </div>

      <AnimatePresence>
        {isExpenseModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsExpenseModalOpen(false)}
              className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-[32px] border border-zinc-100 bg-white shadow-2xl transition-colors dark:border-zinc-900 dark:bg-black"
            >
              <div className="flex items-center justify-between border-b border-zinc-100 p-6 dark:border-zinc-900">
                <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                  {editingExpenseId ? 'Edit Record' : 'Add Record'}
                </h3>
                <button
                  onClick={() => setIsExpenseModalOpen(false)}
                  className="rounded-full p-2 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
                >
                  <X className="h-5 w-5 text-zinc-400" />
                </button>
              </div>
              <div className="overflow-y-auto p-6">
                <AddExpenseForm
                  newExpense={newExpense}
                  setNewExpense={setNewExpense}
                  isAddingExpense={isAddingExpense}
                  handleAddExpense={handleAddExpense}
                  editingExpenseId={editingExpenseId}
                  expenseReceiptRef={expenseReceiptRef}
                  itemInputRef={itemInputRef}
                  currency={currency}
                  onClose={() => setIsExpenseModalOpen(false)}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* List of Expenses */}
      <ExpenseList
        expenses={expenses}
        handleDeleteExpense={handleDeleteExpense}
        handleEditExpense={handleEditExpense}
        currency={currency}
        overseasCurrency={overseasCurrency}
      />
    </div>
  );
}
